import { Fragment, useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useDispatch } from "react-redux";
import { Link, useHistory, useParams } from "react-router-dom";
import {
  FiArrowDown,
  FiArrowUp,
  FiEdit2,
  FiTrash,
  FiPlus,
} from "react-icons/fi";
import swal from "@sweetalert/with-react";
import Button from "../../../components/Button";
import Card from "../../../components/Card";
import Loader from "../../../components/Loader";
import BigLoader from "../../../components/BigLoader";
import { setInterface } from "../../../redux/slices/interfaceSlice";
import service from "../../../service";

export default function DeepReadBab() {
  const { id: deepReadId, bookId } = useParams();
  const dispatch = useDispatch();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [book, setBook] = useState(null);
  const mountedRef = useRef(true);
  const [savingOrder, setSavingOrder] = useState(false);
  const [hoverId, setHoverId] = useState(null);

  const fetchBook = () => {
    setLoading(true);
    service
      .get(`/api/deep-read/${deepReadId}/books/${bookId}`)
      .then((res) => {
        if (!mountedRef.current) return;
        setBook(res.data);
        setLoading(false);
      })
      .catch((err) => {
        if (!mountedRef.current) return;
        setLoading(false);
        if (err.response?.status === 404) {
          history.replace("/deep-read");
        }
      });
  };

  const onDelete = (ringId) => {
    swal({
      title: "Anda yakin?",
      text: "Anda akan menghapus ring ini",
      icon: "warning",
      buttons: ["Batal", true],
      dangerMode: true,
    }).then((value) => {
      if (value) {
        setLoadingDelete(true);
        service
          .delete(`/api/deep-read/rings/${ringId}`)
          .then(() => {
            if (!mountedRef.current) return;
            swal("Berhasil", "Ring telah dihapus", "success");
            fetchBook();
            setLoadingDelete(false);
          })
          .catch(() => {
            if (!mountedRef.current) return;
            setLoadingDelete(false);
          });
      }
      });
  };

  const reorderRing = (direction, index) => {
    const rings = book?.rings || [];
    const swapWith = direction === "up" ? index - 1 : index + 1;
    if (swapWith < 0 || swapWith >= rings.length) return;
    const ordered = [...rings];
    ordered[swapWith] = ordered.splice(index, 1, ordered[swapWith])[0];
    const withPositions = ordered.map((ring, idx) => ({ ...ring, position: idx + 1 }));
    setSavingOrder(true);
    Promise.all(
      withPositions.map((ring, idx) =>
        service.put(`/api/deep-read/rings/${ring.id}`, { position: idx + 1 })
      )
    )
      .then(() => setBook((prev) => (prev ? { ...prev, rings: withPositions } : prev)))
      .finally(() => setSavingOrder(false));
  };

  useEffect(() => {
    const pageTitle = `Kelola Ring${book?.title ? ` · ${book.title}` : ""}`;
    dispatch(
      setInterface({
        active: "Deep Read",
        pageTitle,
      })
    );
    return () => {
      mountedRef.current = false;
    };
  }, [dispatch, book?.title]);

  useEffect(() => {
    fetchBook();
  }, []);

  return (
    <Fragment>
      <Helmet>
        <title>Kelola Ring</title>
      </Helmet>
      <Card>
        <div className="flex items-center mb-5">
          <h3 className="text-lg lg:text-xl font-bold monda flex-1">Daftar Ring</h3>
          <Button
            link
            to={`/deep-read/${deepReadId}/book/${bookId}/ring/add`}
            color="green"
            noFixedHeight
          >
            <FiPlus className="mr-1" /> Tambah Ring
          </Button>
        </div>
        {loading ? (
          <div className="h-40 flex justify-center items-center">
            <Loader className="bg-black-100" />
          </div>
        ) : book?.rings?.length ? (
          <div className="border border-white-300 rounded-sm p-2 flex flex-col gap-0 ml-2">
            {book.rings.map((ring, idx) => (
              <div
                key={ring.id}
                onMouseEnter={() => setHoverId(ring.id)}
                onMouseLeave={() => setHoverId(null)}
                className={`flex items-center gap-2 py-2 border-b last:border-b-0 border-white-200 ${
                  hoverId === ring.id ? "bg-white-200" : ""
                }`}
              >
                <div className="font-bold flex-1 p-2">{ring.title}</div>
                  <div className="flex items-center flex-wrap gap-2">
                    <Link
                      to={`/deep-read/ring/${ring.id}`}
                      className="p-1.5 rounded-sm border border-white-300 text-sm transition-colors hover:bg-primary-500 hover:border-primary-600 hover:text-white-100 flex items-center mt-1"
                    >
                      <FiEdit2 />
                  </Link>
                  <button
                    type="button"
                    onClick={() => onDelete(ring.id)}
                    className="p-1.5 rounded-sm border border-white-300 text-sm transition-colors hover:bg-red-200 hover:border-red-300 hover:text-white-100 flex items-center mt-1"
                  >
                    <FiTrash />
                  </button>
                  <div className="flex mt-1 mr-2">
                    <button
                      type="button"
                      onClick={() => reorderRing("up", idx)}
                      disabled={idx === 0}
                      className="p-1.5 rounded-l-sm border border-white-300 text-sm transition-colors hover:bg-yellow-200 hover:border-yellow-300 hover:text-white-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    >
                      <FiArrowUp />
                    </button>
                    <button
                      type="button"
                      onClick={() => reorderRing("down", idx)}
                      disabled={idx === (book.rings?.length || 0) - 1}
                      className="p-1.5 rounded-r-sm border border-l-0 border-white-300 text-sm transition-colors hover:bg-yellow-200 hover:border-yellow-300 hover:text-white-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    >
                      <FiArrowDown />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-10">Belum ada ring</div>
        )}
      </Card>
      {(loadingDelete || savingOrder) && <BigLoader />}
    </Fragment>
  );
}
